import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Receipt } from "lucide-react";

const emptyExpense = {
  title: "", category: "maintenance", amount: 0, date: "", description: "",
  vehicle_id: "", vehicle_name: "", payment_method: "cash", receipt_number: "", status: "pending"
};

const categories = [
  { value: "maintenance", label: "Maintenance" },
  { value: "fuel", label: "Fuel" },
  { value: "insurance", label: "Insurance" },
  { value: "tolls", label: "Tolls & Fees" },
  { value: "salary", label: "Salary" },
  { value: "parts", label: "Parts & Spares" },
  { value: "permits", label: "Permits" },
  { value: "other", label: "Other" },
];

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyExpense });

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/expenses`),
      axios.get(`${API}/vehicles`)
    ]).then(([expRes, vRes]) => {
      setExpenses(expRes.data);
      setVehicles(vRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    try {
      if (editing) { await axios.put(`${API}/expenses/${editing.id}`, form); toast.success("Updated"); }
      else { await axios.post(`${API}/expenses`, form); toast.success("Expense added"); }
      setDialogOpen(false); setEditing(null); setForm({ ...emptyExpense }); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try { await axios.delete(`${API}/expenses/${id}`); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }
  };

  const openEdit = (e) => { setEditing(e); setForm({ ...e }); setDialogOpen(true); };
  const openNew = () => { setEditing(null); setForm({ ...emptyExpense }); setDialogOpen(true); };

  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const byCategory = {};
  expenses.forEach(e => { byCategory[e.category] = (byCategory[e.category] || 0) + (e.amount || 0); });

  return (
    <div data-testid="expenses-page">
      <PageHeader title="Expenses" subtitle={`${expenses.length} entries`}>
        <button data-testid="create-expense-button" onClick={openNew}
          className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95">
          <Plus className="w-3.5 h-3.5" /> Add Expense
        </button>
      </PageHeader>

      <div className="p-6">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="border border-zinc-800 bg-[#18181B] rounded-sm p-4">
            <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Total Expenses</p>
            <p className="font-heading text-xl font-bold text-white" data-testid="total-expenses">${totalExpenses.toLocaleString()}</p>
          </div>
          {Object.entries(byCategory).slice(0, 3).map(([cat, amount]) => (
            <div key={cat} className="border border-zinc-800 bg-[#18181B] rounded-sm p-4">
              <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 capitalize">{cat}</p>
              <p className="font-heading text-xl font-bold text-white">${amount.toLocaleString()}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-20">
            <Receipt className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No expenses yet.</p>
          </div>
        ) : (
          <div className="border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-900/50 border-b border-zinc-800">
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Date</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Title</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Category</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Amount</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Payment</th>
                  <th className="text-left px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 text-[10px] text-zinc-500 uppercase tracking-wider font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-800/30 hover:bg-zinc-800/20" data-testid={`expense-row-${e.id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-400">{e.date || "—"}</td>
                    <td className="px-4 py-2.5 text-zinc-200">{e.title}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs capitalize">{e.category}</td>
                    <td className="px-4 py-2.5 font-mono text-xs text-orange-400 font-medium">${(e.amount || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-zinc-400 text-xs capitalize">{e.payment_method}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={e.status} /></td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`edit-expense-${e.id}`} onClick={() => openEdit(e)} className="text-zinc-500 hover:text-zinc-200 p-1 mr-1"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button data-testid={`delete-expense-${e.id}`} onClick={() => handleDelete(e.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#18181B] border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">{editing ? "Edit Expense" : "Add Expense"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Title *</Label>
              <Input data-testid="expense-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Category</Label>
                <select data-testid="expense-category-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Amount ($)</Label>
                <Input data-testid="expense-amount-input" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: Number(e.target.value) })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Date</Label>
                <Input data-testid="expense-date-input" type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Payment Method</Label>
                <select data-testid="expense-payment-select" value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Vehicle (optional)</Label>
                <select data-testid="expense-vehicle-select" value={form.vehicle_id} onChange={e => {
                  const v = vehicles.find(vh => vh.id === e.target.value);
                  setForm({ ...form, vehicle_id: e.target.value, vehicle_name: v ? v.registration_number : "" });
                }} className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="">None</option>
                  {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number} - {v.make} {v.model}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                <select data-testid="expense-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Receipt #</Label>
              <Input data-testid="expense-receipt-input" value={form.receipt_number} onChange={e => setForm({ ...form, receipt_number: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Description</Label>
              <Textarea data-testid="expense-description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-expense" onClick={handleSave} className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                {editing ? "Update" : "Add"} Expense
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
